import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart2 } from 'lucide-react';

const StatsDashboard = ({ stats = {}, tier = 'access' }) => {
    // Generate plausable default data if missing
    const {
        favoritesCount = 0,
        totalSets = 120,
        photographersSeen = 1,
        memberDayCount = 563
    } = stats;

    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Tier Logic
    const isClubOrHigher = tier === 'club' || tier === 'executive';
    const isExecutive = tier === 'executive';

    // Mock Data for Milestones
    const milestones = [
        { id: 1, label: 'INITIATION', unlocked: true },
        { id: 2, label: 'CLUB ACCESS', unlocked: isClubOrHigher, remaining: 'LOCKED' },
        { id: 3, label: 'VAULT KEY', unlocked: isExecutive, remaining: 'EXECUTIVE ONLY' },
        { id: 4, label: '100% ARCHIVE', unlocked: false, remaining: `${totalSets - favoritesCount} REMAINING` },
    ];

    // Stats Grid Items
    const statCards = [
        { label: 'MEMBER DAY', value: memberDayCount },
        { label: 'PHOTOGRAPHERS', value: photographersSeen },
        { label: 'COLLECTION', value: favoritesCount },
        { label: 'TOTAL SETS', value: totalSets }
    ];

    // Animation Variants
    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <motion.div
            className="w-full h-full flex flex-col bg-[#e8e6df] font-sans select-none p-6"
            initial="hidden"
            animate={isMounted ? "visible" : "hidden"}
            variants={containerVariants}
        >
            {/* Header */}
            <motion.div className="flex items-center justify-between mb-8 border-b border-[#bdbaa8] pb-4" variants={itemVariants}>
                <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-[#333] rounded flex items-center justify-center text-white shadow-lg">
                        <BarChart2 size={16} />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold uppercase tracking-widest text-[#333]">Membership Stats</h2>
                        <p className="text-[9px] text-[#666] uppercase tracking-wider">Account Metrics</p>
                    </div>
                </div>
                <div className="flex space-x-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#333]"></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-[#bdbaa8]"></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-[#bdbaa8]"></div>
                </div>
            </motion.div>

            {/* Archive Metrics */}
            <motion.div className="mb-6" variants={itemVariants}>
                {/* Archive Status with Settings Style */}
                <div className="bg-[#f4f1ea] rounded-lg p-6 border border-transparent shadow-[0_2px_5px_rgba(0,0,0,0.05)] relative overflow-hidden group">
                    <h3 className="text-xs font-bold text-[#333] uppercase tracking-wide mb-4">Archive Access</h3>

                    {/* Horizontal Meter */}
                    <div className="relative h-16 border border-[#bdbaa8] rounded bg-[#e8e6df] overflow-hidden">
                        {/* Tick Marks */}
                        <div className="absolute bottom-0 w-full h-full flex justify-between px-2 items-end pb-1 opacity-20">
                            {[...Array(21)].map((_, i) => (
                                <div key={i} className={`w-[1px] bg-black ${i % 5 === 0 ? 'h-3' : 'h-1.5'}`}></div>
                            ))}
                        </div>

                        {/* Needle / Progress Bar */}
                        <motion.div
                            className="absolute top-0 bottom-0 left-0 bg-[#AF2E1B] opacity-10"
                            initial={{ width: '0%' }}
                            animate={{ width: `${(favoritesCount / totalSets) * 100}%` }}
                            transition={{ duration: 1.5, type: "spring", bounce: 0 }}
                        />
                        <motion.div
                            className="absolute top-0 h-full w-[2px] bg-[#AF2E1B] shadow-[0_0_2px_rgba(0,0,0,0.3)] z-10"
                            initial={{ left: '0%' }}
                            animate={{ left: `${(favoritesCount / totalSets) * 100}%` }}
                            transition={{ duration: 1.5, type: "spring", bounce: 0.2 }}
                        >
                            <div className="absolute -top-1 -left-[3px] w-2 h-2 bg-[#AF2E1B] rotate-45"></div>
                        </motion.div>
                    </div>

                    <div className="flex justify-between mt-3 font-mono text-[9px] items-center text-[#666] uppercase tracking-wider">
                        <span className="opacity-50">0</span>
                        <div className="bg-[#e8e6df] px-2 py-1 rounded border border-[#bdbaa8] text-[#333] font-bold">
                            {favoritesCount} <span className="text-[8px] font-normal opacity-60">/ {totalSets} COLLECTED</span>
                        </div>
                        <span className="opacity-50">{totalSets}</span>
                    </div>
                </div>
            </motion.div>


            {/* Milestones */}
            <motion.div className="bg-[#f4f1ea] rounded-lg p-6 border border-transparent shadow-[0_2px_5px_rgba(0,0,0,0.05)] mb-6" variants={itemVariants}>
                <h3 className="text-xs font-bold text-[#333] uppercase tracking-wide mb-6">Milestones</h3>
                <div className="space-y-3">
                    {milestones.map((m, i) => (
                        <motion.div
                            key={m.id}
                            className="flex items-center gap-3"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                        >
                            <div className={`w-3 h-3 rounded-full border-[1.5px] transition-all duration-500 flex items-center justify-center ${m.unlocked
                                ? 'bg-[#D4AF37] border-[#D4AF37] shadow-[0_0_5px_#D4AF37]'
                                : 'bg-transparent border-[#bdbaa8]'
                                }`}>
                                {m.unlocked && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                            </div>
                            <span className={`text-[10px] font-mono tracking-wider uppercase flex-1 flex justify-between ${m.unlocked ? 'text-[#333] font-bold' : 'text-[#888]'
                                }`}>
                                <span>{m.label}</span>
                                {!m.unlocked && <span className="text-[8px] opacity-60 tracking-widest items-center flex gap-1">{m.remaining}</span>}
                            </span>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div className="grid grid-cols-2 gap-3" variants={itemVariants}>
                {statCards.map((stat, i) => (
                    <motion.div
                        key={stat.label}
                        className="bg-[#f4f1ea] rounded-lg p-4 border border-transparent shadow-[0_2px_5px_rgba(0,0,0,0.05)] hover:shadow-md transition-shadow duration-300 relative group"
                        whileHover={{ y: -2 }}
                    >
                        <h3 className="text-[8px] font-bold text-[#888] uppercase tracking-widest mb-2 group-hover:text-[#AF2E1B] transition-colors">
                            {stat.label}
                        </h3>
                        <div className="text-2xl font-mono font-bold tracking-tight text-[#333]">
                            {stat.value}
                        </div>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default StatsDashboard;

